import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { setAppLang } from "@/i18n/appLang";
import { CheckCircle2, XCircle, Download, ShieldCheck, Eraser, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { DevistoLogo } from "@/components/DevistoLogo";
import { PublicQuestion } from "@/components/studio/PublicQuestion";
import { API, beDate, eur, inputCls, btnPrimary, btnGhost, apiError, setCurrency } from "@/lib/studioApi";

export default function PublicQuote() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [comment, setComment] = useState("");
  const [option, setOption] = useState("");
  const [busy, setBusy] = useState("");
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const load = () => {
    axios.get(`${API}/devis/${token}`).then(({ data }) => { setCurrency(data.currency); setAppLang(data.lang); setQuote(data); }).catch(() => setError(true));
  };

  useEffect(load, [token]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return { x: point.clientX - rect.left, y: point.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.strokeStyle = "#0F2645";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDraw = () => { drawing.current = false; };

  const clearSignature = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };

  const shown = (option && !quote?.chosen_option && quote?.options?.find((o) => o.name === option)) || quote || {};

  const accept = async () => {
    if (quote.options?.length && !option) return toast.error("Choisissez l'option qui vous convient avant de signer.");
    if (!name.trim()) return toast.error("Indiquez votre nom pour signer.");
    setBusy("accept");
    try {
      await axios.post(`${API}/devis/${token}/accept`, {
        name, comment, email, option, signature_data: canvasRef.current?.toDataURL("image/png") || "",
      });
      toast.success("Devis accepté. L'entreprise est prévenue.");
      load();
    } catch (e) { toast.error(apiError(e, "Action impossible.")); } finally { setBusy(""); }
  };

  const refuse = async () => {
    setBusy("refuse");
    try {
      await axios.post(`${API}/devis/${token}/refuse`, { name: name || "Client", comment });
      toast.success("Réponse envoyée.");
      load();
    } catch (e) { toast.error(apiError(e, "Action impossible.")); } finally { setBusy(""); }
  };

  if (error) {
    return (
      <div data-testid="public-quote-error" className="font-body min-h-screen bg-[#F1F5F9] flex flex-col items-center justify-center px-6 text-center">
        <XCircle className="w-10 h-10 text-red-500" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-[#0F2645]">Devis introuvable</h1>
        <p className="mt-2 text-sm text-[#5C6A87]">Ce lien n'est plus valide.</p>
      </div>
    );
  }

  if (!quote) return <div className="font-body min-h-screen bg-[#F1F5F9] flex items-center justify-center text-[#5C6A87]">Chargement…</div>;

  const answered = ["accepted", "refused"].includes(quote.status);

  return (
    <div data-testid="public-quote-page" className="font-body min-h-screen bg-[#F1F5F9] pt-12 pb-16 px-5">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between">
          <DevistoLogo className="h-7 w-auto" />
          <span className="text-xs font-semibold text-[#93A0BC]">Devis {quote.number}</span>
        </div>

        <div className="mt-6 rounded-2xl border border-[#E2E7F7] bg-white p-6 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <p className="text-xs font-bold uppercase tracking-wider text-[#3A5CC8]">{quote.company.name}</p>
          <h1 className="mt-2 font-heading text-2xl font-bold tracking-tight text-[#0F2645]">Devis {quote.number}</h1>
          <p className="mt-1 text-sm text-[#5C6A87]">
            Pour {quote.client_name} · {beDate(quote.issue_date)} · valable jusqu'au {beDate(quote.valid_until)}
          </p>
          {quote.site_address && <p className="text-sm text-[#5C6A87]">Chantier : {quote.site_address}</p>}

          {quote.options?.length > 0 && !quote.chosen_option && (
            <div data-testid="public-options" className="mt-5 rounded-2xl border border-[#3A5CC8]/30 bg-[#EEF1FC] p-4">
              <p className="font-heading text-sm font-bold text-[#0F2645]">Ce devis propose {quote.options.length} option{quote.options.length > 1 ? "s" : ""} — choisissez celle qui vous convient</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {quote.options.map((o) => (
                  <label key={o.name} data-testid={`public-option-${o.name}`} className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border bg-white px-4 py-3 transition-colors ${option === o.name ? "border-[#3A5CC8] ring-2 ring-[#3A5CC8]/20" : "border-[#E2E7F7]"}`}>
                    <span className="flex items-center gap-2 text-sm font-semibold text-[#0F2645]"><input type="radio" name="option" className="accent-[#3A5CC8]" checked={option === o.name} onChange={() => setOption(o.name)} /> Option {o.name}</span>
                    <span className="font-mono text-sm font-bold text-[#0F2645]">{eur(o.total)}</span>
                  </label>
                ))}
              </div>
              <p className="mt-2 text-xs text-[#5C6A87]">Les montants incluent les travaux de base et la TVA.</p>
            </div>
          )}
          {quote.chosen_option && <p data-testid="public-chosen-option" className="mt-4 rounded-xl bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">Option retenue : {quote.chosen_option}</p>}

          <div className="mt-5 divide-y divide-slate-100">
            {quote.lines.filter((l) => !(l.option || "").trim() || !quote.chosen_option || (l.option || "").trim() === quote.chosen_option).map((l, i) => (
              <div key={i} data-testid={`public-line-${i}`} className={`py-3 ${(l.option || "").trim() && !quote.chosen_option ? (option === (l.option || "").trim() ? "" : "opacity-50") : ""}`}>
                {(l.option || "").trim() && <span className="mb-1 inline-block rounded-full bg-[#EEF1FC] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#2F4CA8]">Option {l.option}</span>}
                <p className="text-sm text-slate-800">{l.description}</p>
                <p className="mt-0.5 text-xs text-[#5C6A87]">
                  {l.quantity} {l.unit} × {eur(l.unit_price)} · TVA {l.vat_percent} %
                  <span className="float-right font-semibold text-[#0F2645]">{eur(l.quantity * l.unit_price)}</span>
                </p>
              </div>
            ))}
          </div>

          <div className="mt-4 border-t border-[#E2E7F7] pt-4 text-right space-y-1">
            <p className="text-sm text-[#5C6A87]">Total HTVA <span className="font-semibold text-[#0F2645]">{eur(shown.net)}</span></p>
            <p className="text-sm text-[#5C6A87]">TVA <span className="font-semibold text-[#0F2645]">{eur(shown.vat_total)}</span></p>
            <p data-testid="public-total" className="font-heading text-xl font-bold text-[#0F2645]">
              {quote.options?.length && !quote.chosen_option ? (option ? `Total avec option ${option}` : "Total base TVAC") : "Total TVAC"}{" "}
              {eur(shown.total)}
            </p>
          </div>

          {quote.remarks && <p className="mt-4 rounded-xl bg-[#F6F8FD] p-4 text-sm text-[#5C6A87]">{quote.remarks}</p>}
          {quote.conditions && <p className="mt-3 text-xs text-[#93A0BC] leading-relaxed">{quote.conditions}</p>}
          {quote.cgv && (
            <details data-testid="public-cgv" className="mt-4 rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] p-3 text-xs text-[#5C6A87]">
              <summary className="cursor-pointer font-semibold text-[#0F2645]">{quote.cgv.title}</summary>
              <div className="mt-2 space-y-1 leading-relaxed">
                {quote.cgv.text.split("\n").map((line, i) => (/^(article|artikel|artículo|art\.|#)/i.test(line.trim())
                  ? <p key={i} className="pt-2 font-semibold text-[#0F2645]">{line.replace(/^#\s*/, "")}</p>
                  : <p key={i}>{line}</p>))}
              </div>
            </details>
          )}

          <a href={`${API}/devis/${token}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="public-pdf-link" className={`${btnGhost} mt-5`}>
            <Download className="w-3.5 h-3.5" /> Télécharger le PDF
          </a>
        </div>

        {answered ? (
          <div data-testid="public-answered" className={`mt-6 rounded-2xl border p-6 ${quote.status === "accepted" ? "border-green-200 bg-green-50" : "border-[#E2E7F7] bg-white"}`}>
            {quote.status === "accepted" ? (
              <>
                <p className="font-heading text-lg font-bold text-green-800 inline-flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> Devis accepté</p>
                {quote.signature?.name && <p className="mt-1 text-sm text-green-800">Signé par {quote.signature.name} le {beDate(quote.signature.signed_at)}.</p>}
              </>
            ) : (
              <p className="font-heading text-lg font-bold text-[#0F2645] inline-flex items-center gap-2"><XCircle className="w-5 h-5" /> Devis refusé</p>
            )}
          </div>
        ) : (
          <div data-testid="public-sign-block" className="mt-6 rounded-2xl border border-[#E2E7F7] bg-white p-6 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
            <h2 className="font-heading text-lg font-semibold text-[#0F2645]">Votre réponse</h2>
            <input data-testid="sign-name" className={`${inputCls} mt-4`} placeholder="Votre nom et prénom *" value={name} onChange={(e) => setName(e.target.value)} />
            <input data-testid="sign-email" type="email" className={`${inputCls} mt-3`} placeholder="Votre e-mail (facultatif)" value={email} onChange={(e) => setEmail(e.target.value)} />
            <textarea data-testid="sign-comment" rows={2} className={`${inputCls} mt-3`} placeholder="Un commentaire ? (facultatif)" value={comment} onChange={(e) => setComment(e.target.value)} />

            <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-[#5C6A87]">Signature</p>
            <div className="mt-2 rounded-xl border border-dashed border-[#E2E7F7] bg-[#F6F8FD]">
              <canvas
                ref={canvasRef}
                data-testid="sign-canvas"
                width={560}
                height={150}
                className="w-full touch-none rounded-xl"
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={endDraw}
              />
            </div>
            <button type="button" data-testid="sign-clear" onClick={clearSignature} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#5C6A87] hover:text-slate-800 transition-colors">
              <Eraser className="w-3 h-3" /> Effacer
            </button>

            <div className="mt-5 flex flex-wrap gap-3">
              <button type="button" data-testid="public-accept-btn" disabled={busy === "accept"} onClick={accept} className={btnPrimary}>
                {busy === "accept" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Accepter et signer
              </button>
              <button type="button" data-testid="public-refuse-btn" disabled={busy === "refuse"} onClick={refuse} className={btnGhost}>
                <XCircle className="w-3.5 h-3.5" /> Refuser
              </button>
            </div>
            <p className="mt-4 inline-flex items-start gap-2 text-[11px] leading-relaxed text-[#93A0BC]">
              <ShieldCheck className="w-3 h-3 mt-0.5 shrink-0" />
              Signature électronique horodatée (règlement eIDAS). Votre nom, la date et votre adresse IP sont
              conservés comme preuve, uniquement pour ce devis.
            </p>
          </div>
        )}

        {quote.reviews && (
          <section data-testid="public-quote-reviews" className="mt-6 rounded-2xl border border-[#E2E7F7] bg-white p-5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="flex items-center gap-0.5" aria-label={`${quote.reviews.average} sur 5`}>
                {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-4 w-4 ${Math.floor(quote.reviews.average) >= n ? "fill-amber-400 text-amber-400" : "text-[#E2E7F7]"}`} />)}
              </span>
              <span className="font-mono text-sm font-bold tabular-nums text-[#0F2645]">{String(quote.reviews.average).replace(".", ",")}/5</span>
              <span className="text-xs text-[#5C6A87]">{quote.reviews.count} avis client{quote.reviews.count > 1 ? "s" : ""} vérifiés après intervention</span>
            </div>
            {quote.reviews.items.length > 0 && (
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {quote.reviews.items.map((r, i) => (
                  <blockquote key={i} className="rounded-xl bg-[#F6F8FD] px-3.5 py-3 text-sm text-[#0F2645]">
                    <p className="leading-relaxed">« {r.comment} »</p>
                    <footer className="mt-1.5 text-xs text-[#5C6A87]">— {r.name}{r.job ? ` · ${r.job}` : ""}</footer>
                  </blockquote>
                ))}
              </div>
            )}
          </section>
        )}

        <PublicQuestion path={`/devis/${token}`} docLabel="ce devis" defaultName={name} defaultEmail={email} />

        <p className="mt-8 text-center text-xs text-[#93A0BC]">
          Devis préparé avec Devisto · {quote.company.phone} · {quote.company.email}
        </p>
      </div>
    </div>
  );
}
